import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import "../styles/ManageAdvertisingAgent.css";
import SideBar from "../components/SideBar";
import { useNavigate } from "react-router-dom";
import { businessAgentAPI } from "../services/businessAgentService";
import { 
  formatToDDMMYYYY, 
  convertHTMLDateToDDMMYYYY,
  convertDDMMYYYYToHTMLDate 
} from "../utils/dateUtils";

function BusinessAgentRejectedHistory() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [rejectedDate, setRejectedDate] = useState("all");
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRejectedAgents, setTotalRejectedAgents] = useState(0);
  const [filteredAgentsCount, setFilteredAgentsCount] = useState(0);
  const [currentPageAgentsCount, setCurrentPageAgentsCount] = useState(0);
  const itemsPerPage = 6;
  const navigate = useNavigate();

  // Fetch rejected applications
  const fetchRejectedApplications = async () => {
    setLoading(true);
    setError("");
    try {
      const filters = {
        page: currentPage,
        per_page: itemsPerPage,
        status: 'rejected',
        agent_type: type !== 'all' ? type : undefined,
        search: search || undefined
      };

      const response = await businessAgentAPI.getAgentApplications(filters);
      setApplications(response.applications || []);
      
      // Use pagination total as fallback if getBusinessAgentCount fails
      if (response.pagination && response.pagination.total && totalRejectedAgents === 0) {
        setTotalRejectedAgents(response.pagination.total);
      }
    } catch (err) {
      setError("Failed to fetch rejected applications");
      console.error("Error fetching rejected applications:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch total rejected business agent count with filters
  const fetchTotalRejectedAgentCount = async () => {
    try {
      // Prepare filters for the count API
      const filters = {};
      
      if (search) {
        filters.search = search;
      }
      
      if (type !== 'all') {
        filters.agent_type = type;
      }
      
      // Count only rejected applications for this page
      filters.status = "rejected";
      
      const response = await businessAgentAPI.getBusinessAgentCount(filters);
      
      // Assuming the API returns { count: number } or similar structure
      if (response.count !== undefined) {
        setTotalRejectedAgents(response.count);
      } else if (response.total !== undefined) {
        setTotalRejectedAgents(response.total);
      } else {
        // If the response structure is different, try to extract count from the first property
        const count = Object.values(response)[0];
        if (typeof count === 'number') {
          setTotalRejectedAgents(count);
        } else {
          console.warn('Unexpected API response format:', response);
          // Fallback: fetch count from pagination in fetchRejectedApplications
        }
      }
    } catch (error) {
      console.error('Error fetching rejected business agent count:', error);
      // Don't show error to user for count failure, use fallback
    }
  };

  useEffect(() => {
    fetchTotalRejectedAgentCount();
  }, [search, type]);

  useEffect(() => {
    fetchRejectedApplications();
  }, [currentPage, type, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, type, rejectedDate]);

  // Calculate filtered agents count whenever applications change
  useEffect(() => {
    if (applications.length > 0) {
      // Filter applications by rejected date
      const filtered = applications.filter((application) => {
        const searchMatch =
          application.customer_details?.name?.toLowerCase().includes(search.toLowerCase()) ||
          application.customer_details?.email?.toLowerCase().includes(search.toLowerCase());

        const typeMatch = type === "all" || 
          (application.agent_type?.toLowerCase() === type.toLowerCase());

        // Date filtering in dd/mm/yyyy format
        const rejectedDateFormatted = formatToDDMMYYYY(application.reviewed_at);
        const dateMatch = rejectedDate === "all" || rejectedDateFormatted === rejectedDate;

        return searchMatch && dateMatch && typeMatch;
      });

      // Calculate paginated agents
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

      // Update statistics
      setFilteredAgentsCount(filtered.length);
      setCurrentPageAgentsCount(paginated.length);
    } else {
      setFilteredAgentsCount(0);
      setCurrentPageAgentsCount(0);
    }
  }, [applications, currentPage, search, type, rejectedDate]);

  const goToRequestHistory = () => {
    navigate('/manage-business-agent');
  };

  const goToApprovedHistory = () => {
    navigate('/business-agent-approved-history');
  };

  const goToRejectedHistory = () => {
    navigate('/business-agent-rejected-history');
  };

  // Handle date filter change
  const handleDateChange = (e) => {
    const htmlDate = e.target.value;
    const ddmmyyyyDate = convertHTMLDateToDDMMYYYY(htmlDate);
    setRejectedDate(ddmmyyyyDate);
  };

  // Get current HTML date value for the input
  const getHTMLDateValue = () => {
    return rejectedDate === 'all' ? '' : convertDDMMYYYYToHTMLDate(rejectedDate);
  };

  // Filter applications by rejected date
  const filteredApplications = applications.filter((application) => {
    const searchMatch =
      application.customer_details?.name?.toLowerCase().includes(search.toLowerCase()) ||
      application.customer_details?.email?.toLowerCase().includes(search.toLowerCase());

    const typeMatch = type === "all" || 
      (application.agent_type?.toLowerCase() === type.toLowerCase());

    // Date filtering in dd/mm/yyyy format
    const rejectedDateFormatted = formatToDDMMYYYY(application.reviewed_at);
    const dateMatch = rejectedDate === "all" || rejectedDateFormatted === rejectedDate;

    return searchMatch && dateMatch && typeMatch;
  });

  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedApplications = filteredApplications.slice(startIndex, startIndex + itemsPerPage);

  const handleRowClick = (application) => {
    console.log("Navigating to business agent application details:", application);

    navigate("/business-agent-details", {
      state: {
        agent: {
          ...application.customer_details, // Spread customer details
          application_id: application.application_id, // Add application_id to agent object
          agent_type: application.agent_type,
          status: application.status,
          applied_at: application.applied_at,
          reviewed_at: application.reviewed_at,
          rejection_reason: application.rejection_reason,
          photo_url: application.photo_url,
          is_already_agent: application.is_already_agent,
          notes: application.notes,
          commission_rate: application.commission_rate,
        },
        application: application, // Pass full application data
        isRejectedApplication: true,
      },
    });
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Business Agent</h2>
          </div>

          {/* Rejected History Statistics Section */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Rejected Applications:</span>
              <span className="stat-value">{totalRejectedAgents}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Applications:</span>
              <span className="stat-value">{filteredAgentsCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">
                {paginatedApplications.length}
              </span>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by Type:</label>
              <select value={type} onChange={(e) => setType(e.target.value)} className="filter-select">
                <option value="all">All</option>
                <option value="mini">Mini Business Agent</option>
                <option value="senior">Senior Business Agent</option>
              </select>
            </div>

            <div className="input-group">
              <label>Filter by Rejected Date:</label>
              <input
                type="date"
                value={getHTMLDateValue()}
                onChange={handleDateChange}
                className="date-filter-select"
              />
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="agent-top-bar">
            <div className="top-buttons">
              <button className="tab-btn" onClick={goToRequestHistory}>
                Request History
              </button>
              <button className="tab-btn" onClick={goToApprovedHistory}>
                Approved History
              </button>
              <button className="tab-btn active" onClick={goToRejectedHistory}>
                Rejected History
              </button>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Rejected Date</th>
                <th>Reason</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Applications...</div>
                  </td>
                </tr>
              ) : paginatedApplications.length > 0 ? (
                paginatedApplications.map((application, i) => (
                  <tr 
                    key={application.application_id || i} 
                    onClick={() => handleRowClick(application)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{application.customer_details?.first_name || application.customer_details?.name}</td>
                    <td>{application.customer_details?.email}</td>
                    <td>
                      {application.agent_type === 'senior' 
                        ? 'Senior Business Agent' 
                        : 'Mini Business Agent'}
                    </td>
                    <td>{formatToDDMMYYYY(application.reviewed_at)}</td>
                    <td>{application.rejection_reason || 'No reason provided'}</td>
                    <td>
                      <span className={`status rejected`} style={{color:"red"}}>
                        Rejected
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    {filteredAgentsCount === 0 && totalRejectedAgents > 0 
                      ? "No rejected applications match your filters" 
                      : "No rejected applications found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination - Only show when there are filtered applications */}
          {!loading && filteredAgentsCount > 0 && (
            <div className="pagination">
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BusinessAgentRejectedHistory;